import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { eur } from "@/lib/format";
import { ArrowLeft, User, MapPin, Receipt, Building2, Phone, Mail } from "lucide-react";

const Field = ({ label, value }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-[#6a6d70]">{label}</div>
    <div className="mt-0.5 text-sm text-[#32363a]">{value || "—"}</div></div>
);

const PartnerCard = ({ icon: Icon, title, p, accent }) => (
  <div className="rounded-lg border border-[#e5e9f0] bg-white p-4">
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: accent }}>
      <Icon size={14} /> {title}
    </div>
    <div className="mt-2 text-sm font-medium text-[#32363a]">{p?.name}</div>
    <div className="font-mono text-[10px] text-[#6a6d70]">{p?.partner_code}</div>
    {p?.street && <div className="mt-1 text-xs text-[#454b52]">{p.street}<br />{p.postal_code} {p.city}, {p.country}</div>}
  </div>
);

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);

  useEffect(() => { api.get(`/customers/${id}`).then((r) => setC(r.data)).catch(() => setC(false)); }, [id]);

  if (c === null) return <Loader label="Loading customer…" />;
  if (c === false) return <div className="p-8 text-[#6a6d70]">Customer not found.</div>;

  const pf = c.partner_functions || {};
  const creditPct = Math.min(100, Math.round(((c.credit_used || 0) / c.credit_limit) * 100));

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/customers")} data-testid="cust-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-[#0a6ed1] hover:underline"><ArrowLeft size={16} /> Business Partners</button>

      {/* Header */}
      <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#7c3aed]/12 text-[#7c3aed]"><User size={24} /></div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-[#32363a]">{c.name}</h1>
              <p className="mt-0.5 font-mono text-sm text-[#6a6d70]">{c.customer_code} · {c.customer_group}</p>
            </div>
          </div>
          <div className="text-right"><div className="text-[11px] uppercase text-[#6a6d70]">Credit Limit</div><div className="text-2xl font-semibold text-[#7c3aed]">{eur(c.credit_limit)}</div></div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6 border-t border-[#eff1f4] pt-4">
          <Field label="Industry" value={c.industry} />
          <Field label="Sales Org" value={c.sales_org} />
          <Field label="Distribution" value={c.distribution_channel} />
          <Field label="Division" value={c.division} />
          <Field label="Incoterms" value={c.incoterms} />
          <Field label="Payment Terms" value={c.payment_terms} />
          <Field label="Currency" value={c.currency} />
          <Field label="VAT / Tax No." value={c.tax_number} />
          <Field label="Country" value={c.country} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm" data-testid="cust-sales-area">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Sales Area (KNVV)</h2>
          <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Cust. Pricing Proc." value={c.customer_pricing_proc} />
            <Field label="Price Group" value={c.price_group} />
            <Field label="Stat. Group" value={c.cust_stat_group} />
            <Field label="Delivery Priority" value={c.delivery_priority} />
            <Field label="Shipping Cond." value={c.shipping_condition} />
            <Field label="Order Probability" value={c.order_probability != null ? `${c.order_probability}%` : "—"} />
            <Field label="Acct Assign. Group" value={c.acct_assignment_group} />
            <Field label="Tax Classification" value={c.tax_classification} />
          </div>
        </div>
        <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm" data-testid="cust-company-code">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Company Code / FI (KNB1)</h2>
          <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Company Code" value={c.company_code} />
            <Field label="Account Group" value={c.account_group} />
            <Field label="Recon. Account" value={c.recon_account} />
            <Field label="Sort Key" value={c.sort_key} />
            <Field label="Dunning Procedure" value={c.dunning_procedure} />
            <Field label="Dunning Level" value={c.dunning_level} />
            <Field label="Payment Method" value={c.payment_method} />
            <Field label="Tolerance Group" value={c.tolerance_group} />
            <Field label="Overdue" value={eur(c.overdue || 0)} />
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Contact + credit */}
        <div className="space-y-4">
          <div className="rounded-lg border border-[#e5e9f0] bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Primary Contact</h2>
            <div className="mt-3 text-sm font-medium text-[#32363a]">{c.contact?.name}</div>
            <div className="mt-2 flex items-center gap-2 text-xs text-[#454b52]"><Mail size={13} className="text-[#6a6d70]" /> {c.contact?.email}</div>
            <div className="mt-1 flex items-center gap-2 text-xs text-[#454b52]"><Phone size={13} className="text-[#6a6d70]" /> {c.contact?.phone}</div>
          </div>
          <div className="rounded-lg border border-[#e5e9f0] bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Credit Management</h2>
            <div className="mt-3 flex justify-between text-xs text-[#6a6d70]"><span>Used {eur(c.credit_used || 0)}</span><span>{creditPct}%</span></div>
            <div className="mt-1 h-2 rounded bg-[#eef1f5]"><div className={`h-2 rounded ${creditPct > 80 ? "bg-[#bb0000]" : "bg-[#7c3aed]"}`} style={{ width: `${creditPct}%` }} /></div>
            <div className="mt-2 text-xs text-[#89919a]">Available {eur(c.credit_limit - (c.credit_used || 0))} of {eur(c.credit_limit)}</div>
          </div>
        </div>

        {/* Partner functions */}
        <div className="lg:col-span-2">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Partner Functions</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <PartnerCard icon={User} title="Sold-To Party" p={pf.sold_to} accent="#0a6ed1" />
            <PartnerCard icon={Receipt} title="Bill-To Party" p={pf.bill_to} accent="#7c3aed" />
            <PartnerCard icon={Building2} title="Payer" p={pf.payer} accent="#107e3e" />
            {(pf.ship_to || []).map((st, i) => (
              <PartnerCard key={i} icon={MapPin} title={`Ship-To Party ${i + 1}`} p={st} accent="#e9730c" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
